if (!customElements.get('sondos-complementary-carousel')) {
  customElements.define('sondos-complementary-carousel', class SondosComplementaryCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-spc-track]');
      this.slides = Array.from(this.querySelectorAll('[data-spc-slide]'));
      this.previousButton = this.querySelector('[data-spc-previous]');
      this.nextButton = this.querySelector('[data-spc-next]');
      this.dots = Array.from(this.querySelectorAll('[data-spc-dot]'));
      this.titleLinks = Array.from(this.querySelectorAll('[data-spc-title-ar]'));
      if (!this.track) return;

      this.activeIndex = 0;
      this.ticking = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.applyTitleFallbacks();
      document.fonts?.ready.then(() => {
        if (this.isConnected) this.applyTitleFallbacks();
      });

      this.resizeObserver = new ResizeObserver(() => {
        this.applyTitleFallbacks();
        if (this.slides.length > 1) this.queueUpdate();
      });
      this.resizeObserver.observe(this.track);

      if (this.slides.length < 2) return;

      this.previousButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
      this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
      this.updateState();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    queueUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.updateState();
        this.ticking = false;
      });
    }

    // Arabic title always (owner, 2026-10-17: «خليه عربي»): the card CSS clamps it to two lines with an ellipsis.
    // The old fallback measured every title with an off-screen probe and swapped long names for the English one.
    applyTitleFallbacks() {
      this.titleLinks.forEach((link) => {
        const arabicTitle = link.dataset.spcTitleAr?.trim();
        if (!arabicTitle) return;
        link.closest('.spc-card__title')?.classList.remove('has-secondary');
        link.classList.remove('has-secondary', 'is-english');
        if (link.textContent !== arabicTitle) link.textContent = arabicTitle;
        link.lang = 'ar';
        link.dir = 'rtl';
      });
    }

    updateState() {
      const trackEdge = this.track.getBoundingClientRect().right;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().right - trackEdge);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      this.activeIndex = closestIndex;
      this.dots.forEach((dot, index) => {
        const isActive = index === closestIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.previousButton) this.previousButton.disabled = closestIndex === 0;
      if (this.nextButton) this.nextButton.disabled = closestIndex === this.slides.length - 1;
    }

    goTo(index) {
      const safeIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      this.slides[safeIndex].scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }
  });
}

// v6 list loader (2026-10-17, owner: «القسم بقعد فترة عشان يظهر»): the list is fetched as soon as the page has finished
// loading (idle), not when the shopper reaches the section, so it is ready before she scrolls to it. A shopper who
// scrolls there first triggers it at once. One request; the reserved box (CSS) keeps the page from shifting.
if (!customElements.get('sondos-fbt-loader')) {
  customElements.define('sondos-fbt-loader', class SondosFbtLoader extends HTMLElement {
    connectedCallback() {
      if (this.started || !this.dataset.url) return;
      const start = () => this.load();
      const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(start, { timeout: 2000 }) : setTimeout(start, 200));
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
      this.io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) start(); }, { rootMargin: '0px 0px 800px 0px' });
      this.io.observe(this);
    }

    disconnectedCallback() {
      this.io?.disconnect();
    }

    load() {
      if (this.started) return;
      this.started = true;
      this.io?.disconnect();
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const list = html.querySelector('product-recommendations');
          if (list && list.innerHTML.trim().length) {
            this.innerHTML = list.innerHTML;
            // measurement (2026-10-18): the journey tracker starts observing the cards; observation only
            document.dispatchEvent(new CustomEvent('sondos:fbt:loaded'));
          }
        })
        .catch((error) => console.error(error))
        .finally(() => this.removeAttribute('data-spc-pending'));
    }
  });
}
